import { useMemo } from 'react';

/**
 * The `${name}_column-order` entry in localStorage is seeded once, with
 * whatever columns existed on the user's first visit, and dragging a column
 * that the stored order does not mention is silently ignored by the table
 * settings menu (FES-1089: `actions` is permission-gated, so orders seeded
 * without it left the column impossible to reorder).
 *
 * Merge instead of reset: keep the user's stored positions for columns that
 * are still present, slot the missing ones in at their default spot, and only
 * force an overwrite when the stored order actually needs the repair.
 */
export const mergeStoredColumnOrder = ({ storedOrder, headerIds }) => {
  const naturalColumnOrder = {};
  headerIds.forEach((id, index) => {
    naturalColumnOrder[id] = index;
  });

  if (!storedOrder) {
    return {
      initialColumnsOrder: naturalColumnOrder,
      shouldForceOverwrite: true,
    };
  }

  const hasMissingIds = headerIds.some((id) => storedOrder[id] === undefined);
  if (!hasMissingIds) {
    return {
      initialColumnsOrder: naturalColumnOrder,
      shouldForceOverwrite: false,
    };
  }

  const initialColumnsOrder = {};
  headerIds
    .map((id, headerIndex) => ({
      id,
      headerIndex,
      sortKey: storedOrder[id] ?? headerIndex,
    }))
    .sort((a, b) => a.sortKey - b.sortKey || a.headerIndex - b.headerIndex)
    .forEach(({ id }, index) => {
      initialColumnsOrder[id] = index;
    });

  return { initialColumnsOrder, shouldForceOverwrite: true };
};

const useInitialColumnsOrder = ({ name, headers }) => {
  return useMemo(() => {
    let storedOrder = null;
    try {
      const raw = localStorage.getItem(`${name}_column-order`);
      const parsed = raw ? JSON.parse(raw) : null;
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        storedOrder = parsed;
      }
    } catch {
      storedOrder = null;
    }

    return mergeStoredColumnOrder({
      storedOrder,
      headerIds: headers.map(({ id }) => id),
    });
  }, [name, headers]);
};

export default useInitialColumnsOrder;
